const schemaUtils = require('./dataDictionaryService/schemaUtils');

/**
 * Extract CRUD access information from metadata namespaces/relations
 * This is used by lib_security-client to check access to specific URLs and methods
 * Unlike the full OpenAPI spec, this returns only minimal access information
 *
 * This function respects feature flags and role-based visibility by:
 * 1. Building a schema from hNodes (which applies feature flag filtering)
 * 2. Only including relations that have corresponding schemas (i.e., weren't hidden)
 *
 * @param {object} metadata - The use case metadata containing namespaces and hNodes
 * @param {string} role - The role to extract access for (USER, ADMIN, SUPPORT, or custom role)
 * @param {object} [prebuiltSchema] - Optional pre-built schema to avoid duplicate schema building
 * @param {{ featureFlags?: string[] }} [options] - Runtime context used for feature-flag-aware UI visibility
 * @returns {object} Route access information keyed by namespace/relation
 */
function getRouteAccess(metadata, role, prebuiltSchema, options = {}) {
  if (!metadata || !metadata.namespaces) {
    throw new Error('Metadata must contain a namespaces array');
  }

  // Custom roles should be treated as ADMIN for access purposes
  const effectiveRole = ['USER', 'ADMIN', 'SUPPORT', 'DEVICE', 'DEITY', 'SYSTEM'].includes(role) ? role : 'ADMIN';
  const isPrivilegedRole = ['SUPPORT', 'DEITY'].includes(effectiveRole);

  if (isPrivilegedRole) {
    const routeAccess = { namespaces: [] };
    metadata.namespaces.forEach(namespace => {
      const namespaceAccess = { title: namespace.title, relations: [] };
      namespace.relations?.forEach(relation => {
        namespaceAccess.relations.push(extractRelationAccess(relation, effectiveRole));
      });
      if (namespaceAccess.relations.length) {
        routeAccess.namespaces.push(namespaceAccess);
      }
    });
    if (options.includeVirtualRfidRelation) addRfidRelationAccess(routeAccess);
    return routeAccess;
  }

  // Build schema from hNodes to determine which relations are actually accessible.
  // If a prebuilt schema is provided, use it to avoid duplicate schema building.
  const {
    rootSchema,
    schemasExist: fallbackSchemasExist,
    canApplyUiIntersection
  } = buildSchemaContext(metadata, effectiveRole, prebuiltSchema, options);

  const { existenceMap: schemasExist, schemaCapabilitiesMap } = canApplyUiIntersection
    ? extractSchemaCapabilitiesAndExistence(rootSchema)
    : { existenceMap: fallbackSchemasExist, schemaCapabilitiesMap: {} };

  const routeAccess = {
    namespaces: []
  };

  // Extract access from each namespace
  metadata.namespaces.forEach(namespace => {
    const namespaceAccess = {
      title: namespace.title,
      relations: []
    };

    // Extract access from each relation in the namespace
    if (namespace.relations && Array.isArray(namespace.relations)) {
      namespace.relations.forEach(relation => {
        const schemaName = `${namespace.title}.${relation.title}`;

        // For non-privileged roles, only include relations that have schemas
        // (i.e., relations that weren't filtered out by feature flags or role visibility)
        // Privileged roles can see all relations regardless
        if (!isPrivilegedRole && !schemasExist[schemaName]) {
          return; // Skip this relation
        }

        const relationAccess = extractRelationAccess(relation, effectiveRole);

        // There are two permissions signals here:
        // - methodAccess is the hard relation permission list.
        // - x-routeAccessHints is a visible UI block saying "this route is needed here."
        // We require the schema capability check too, so a hidden or read-only button/form
        // cannot accidentally turn a backend route on for the current role.
        if (canApplyUiIntersection) {
          const schemaCapabilities = schemaCapabilitiesMap[schemaName] || {
            get: true,
            post: false,
            patch: false,
            remove: false
          };

          relationAccess.methodAccess = {
            get: (!!relationAccess.methodAccess.get || !!schemaCapabilities.getHint) && !!schemaCapabilities.get,
            post: (!!relationAccess.methodAccess.post || !!schemaCapabilities.postHint) && !!schemaCapabilities.post,
            patch: (!!relationAccess.methodAccess.patch || !!schemaCapabilities.patchHint) && !!schemaCapabilities.patch,
            remove: (!!relationAccess.methodAccess.remove || !!schemaCapabilities.removeHint) && !!schemaCapabilities.remove
          };
        }

        // Always include the relation in route access, even if hideApiDocs is true
        // The hideApiDocs flag only affects OpenAPI documentation, not route access
        namespaceAccess.relations.push(relationAccess);
      });
    }

    // Only include namespace if it has relations
    if (namespaceAccess.relations.length > 0) {
      routeAccess.namespaces.push(namespaceAccess);
    }
  });

  if (canApplyUiIntersection) {
    upsertSyntheticRelationAccess(routeAccess, metadata, schemaCapabilitiesMap);
  }

  if (options.includeVirtualRfidRelation) addRfidRelationAccess(routeAccess);
  return routeAccess;
}

function addRfidRelationAccess(routeAccess) {
  let application = routeAccess.namespaces.find(ns => ns.title === 'application');
  if (!application) {
    application = { title: 'application', relations: [] };
    routeAccess.namespaces.push(application);
  }
  const relation = application.relations.find(rel => rel.title === 'rfidEnabledRelation');
  if (relation) {
    relation.methodAccess = { get: true, post: false, patch: false, remove: false };
  } else {
    application.relations.push({ title: 'rfidEnabledRelation', methodAccess: {
      get: true, post: false, patch: false, remove: false
    } });
  }
}

/**
 * Build a map of which namespace.relation schemas actually exist after hNode processing
 * This respects feature flags and role-based visibility
 *
 * @param {object} metadata - The metadata with hNodes
 * @param {string} role - The effective role
 * @param {object} [prebuiltSchema] - Optional pre-built schema to avoid duplicate building
 * @param {{ featureFlags?: string[] }} [options] - Runtime context used for feature-flag-aware UI visibility
 * @returns {object} Map of schema names to boolean existence
 */
function buildSchemaContext(metadata, role, prebuiltSchema, options = {}) {
  // If a prebuilt schema was provided, use it directly.
  let rootSchema = prebuiltSchema;
  const featureFlags = resolveFeatureFlags(metadata, options);

  const buildContext = {
    'identity:role': { title: role },
    allFeatureFlags: featureFlags
  };

  // Otherwise, build the schema if hNode tree exists.
  if (!rootSchema) {
    const nodeTree = metadata.hNodes;
    if (!nodeTree) {
      // If no hNode tree exists, assume all schemas exist (backward compatibility)
      // and DO NOT attempt to apply UI-derived intersection.
      const existenceMap = {};
      metadata.namespaces?.forEach(ns => {
        ns.relations?.forEach(rel => {
          existenceMap[`${ns.title}.${rel.title}`] = true;
        });
      });
      return { rootSchema: undefined, schemasExist: existenceMap, canApplyUiIntersection: false, buildContext };
    }

    rootSchema = schemaUtils.buildSchema(nodeTree, {
      id: 'urn:backbone:data-dictionary',
      title: metadata.title || 'UseCase',
      context: buildContext
    });
  }

  return { rootSchema, schemasExist: undefined, canApplyUiIntersection: true, buildContext };
}

function resolveFeatureFlags(metadata, options = {}) {
  if (Array.isArray(options.featureFlags)) {
    return options.featureFlags;
  }

  return Array.isArray(metadata?.featureFlags) ? metadata.featureFlags : [];
}

function upsertSyntheticRelationAccess(routeAccess, metadata, schemaCapabilitiesMap) {
  const declaredRelations = new Set();
  metadata.namespaces.forEach(namespace => {
    namespace.relations?.forEach(relation => {
      declaredRelations.add(`${namespace.title}.${relation.title}`);
    });
  });

  const syntheticSchemaNames = new Set(Object.keys(schemaCapabilitiesMap || {}));

  syntheticSchemaNames.forEach(schemaName => {
    if (declaredRelations.has(schemaName)) return;

    const [namespaceTitle, relationTitle] = schemaName.split('.');
    if (!namespaceTitle || !relationTitle) return;

    const schemaCapabilities = schemaCapabilitiesMap[schemaName] || {
      get: false,
      post: false,
      patch: false,
      remove: false
    };
    const uiCapabilities = schemaCapabilities;
    if (!uiCapabilities.get && !uiCapabilities.post && !uiCapabilities.patch && !uiCapabilities.remove) return;

    let namespace = routeAccess.namespaces.find(ns => ns.title === namespaceTitle);
    if (!namespace) {
      namespace = { title: namespaceTitle, relations: [] };
      routeAccess.namespaces.push(namespace);
    }

    if (namespace.relations.some(rel => rel.title === relationTitle)) return;

    namespace.relations.push({
      title: relationTitle,
      methodAccess: {
        get: !!uiCapabilities.get,
        post: !!uiCapabilities.post,
        patch: !!uiCapabilities.patch,
        remove: !!uiCapabilities.remove
      }
    });
  });
}

/**
 * Extract UI capabilities and schema existence from built schema
 * @param {object} rootSchema - The root schema containing namespaces or $defs
 * @returns {object} Object with existenceMap and schemaCapabilitiesMap
 */
function extractSchemaCapabilitiesAndExistence(rootSchema) {
  const existenceMap = {};
  const schemaCapabilitiesMap = {};

  const namespaces = rootSchema?.properties;
  if (namespaces && typeof namespaces === 'object' && Object.keys(namespaces).length) {
    Object.entries(namespaces).forEach(([namespace, nsSchema]) => {
      const relProps = nsSchema?.properties || {};
      Object.entries(relProps).forEach(([relation, relNode]) => {
        const schemaName = `${namespace}.${relation}`;
        existenceMap[schemaName] = true;
        schemaCapabilitiesMap[schemaName] = getSchemaCapabilitiesForRelation(rootSchema, relNode);
      });
    });
    return { existenceMap, schemaCapabilitiesMap };
  }

  // Backward-compatible fallback: some callers may provide only $defs.
  const defs = rootSchema?.$defs || {};
  Object.entries(defs).forEach(([defName, defSchema]) => {
    existenceMap[defName] = true;
    const { getHint, postHint, patchHint, removeHint, post, patch, remove, readOnly } = getWriteCapabilitiesFromSchema(
      rootSchema,
      defSchema,
      new Set()
    );
    schemaCapabilitiesMap[defName] = {
      get: true,
      getHint: !!getHint,
      postHint: !!postHint,
      patchHint: !!patchHint,
      removeHint: !!removeHint,
      post: readOnly ? false : post,
      patch: readOnly ? false : patch,
      remove: readOnly ? false : remove
    };
  });

  return { existenceMap, schemaCapabilitiesMap };
}

/**
 * Get UI capabilities for a specific relation from its schema
 * @param {object} rootSchema - The root schema containing $defs
 * @param {object} relNode - The relation node from the schema
 * @returns {object} What UI allows based on schema annotations
 */
function getSchemaCapabilitiesForRelation(rootSchema, relNode) {
  const visible = !!relNode;
  if (!visible) return { get: false, post: false, patch: false, remove: false };

  const { getHint, postHint, patchHint, removeHint, post, patch, remove, readOnly } = getWriteCapabilitiesFromSchema(
    rootSchema,
    relNode,
    new Set()
  );

  return {
    get: true,
    getHint: !!getHint,
    postHint: !!postHint,
    patchHint: !!patchHint,
    removeHint: !!removeHint,
    post: readOnly ? false : post,
    patch: readOnly ? false : patch,
    remove: readOnly ? false : remove
  };
}

/**
 * Extract route access hints and write capabilities from schema node and its children
 * Recursively searches for x-accessContext/x-routeAccessHints annotations and handles composition.
 * @param {object} rootSchema - The root schema containing $defs
 * @param {object} node - The schema node to analyze
 * @param {Set} visited - Set of visited nodes to prevent infinite recursion
 * @returns {object} Capabilities (getHint, postHint, patchHint, removeHint, post, patch, remove, readOnly)
 */
function getWriteCapabilitiesFromSchema(rootSchema, node, visited) {
  if (!node || typeof node !== 'object') {
    return emptyWriteCapabilities();
  }

  if (visited.has(node)) {
    return emptyWriteCapabilities();
  }
  visited.add(node);

  const restrictions = node['x-accessRestrictions'];
  const readOnly = !!restrictions?.readOnly;

  const accessContext = node['x-accessContext'];
  // Think of x-accessContext as "what this UI block can do" and x-routeAccessHints
  // as "please expose this HTTP route because this block needs it." Keeping them
  // separate stops passive metadata, like a menu label, from becoming API permission.
  const routeAccessHints = node['x-routeAccessHints'];
  const localGetHint = !readOnly && !!routeAccessHints?.get?.value;
  const localPostHint = !readOnly && !!routeAccessHints?.post?.value;
  const localPatchHint = !readOnly && !!routeAccessHints?.patch?.value;
  const localRemoveHint = !readOnly && !!routeAccessHints?.remove?.value;
  const localPost = !!accessContext?.post?.value;
  const localPatch = !!accessContext?.patch?.value;
  const localRemove = !!accessContext?.remove?.value;

  const base = {
    getHint: localGetHint,
    postHint: localPostHint,
    patchHint: localPatchHint,
    removeHint: localRemoveHint,
    post: localPost,
    patch: localPatch,
    remove: localRemove,
    readOnly
  };

  // Follow $ref into $defs.
  if (typeof node.$ref === 'string' && node.$ref.startsWith('#/$defs/')) {
    const defKey = node.$ref.slice('#/$defs/'.length);
    const defSchema = rootSchema?.$defs?.[defKey];
    const refCaps = getWriteCapabilitiesFromSchema(rootSchema, defSchema, visited);
    return mergeWriteCapabilities(base, refCaps);
  }

  // OR across composition keywords.
  const compositionKeys = ['anyOf', 'oneOf', 'allOf'];
  const composed = compositionKeys.reduce(
    (acc, key) => {
      const arr = node[key];
      if (!Array.isArray(arr)) return acc;
      return arr.reduce((innerAcc, child) => {
        const childCaps = getWriteCapabilitiesFromSchema(rootSchema, child, visited);
        return mergeWriteCapabilities(innerAcc, childCaps);
      }, acc);
    },
      { ...base }
  );

  // Recurse into common containers (conservative; mainly to handle wrappers).
  const fromItems = node.items ? getWriteCapabilitiesFromSchema(rootSchema, node.items, visited) : undefined;
  const fromProperties = node.properties
    ? Object.values(node.properties).reduce(
      (acc, propSchema) => {
        const propCaps = getWriteCapabilitiesFromSchema(rootSchema, propSchema, visited);
        return mergeWriteCapabilities(acc, propCaps);
      },
      emptyWriteCapabilities()
    )
    : undefined;

  const merged = mergeWriteCapabilities(
    composed,
    fromItems || emptyWriteCapabilities()
  );
  return mergeWriteCapabilities(
    merged,
    fromProperties || emptyWriteCapabilities()
  );
}

/**
 * Extract CRUD access for a single relation based on methodAccess configuration
 *
 * @param {object} relation - The relation object from metadata
 * @param {string} role - The effective role (ADMIN, USER, SUPPORT, etc.)
 * @returns {object} Access information for this relation
 */
function extractRelationAccess(relation, role) {
  const relationAccess = {
    title: relation.title,
    methodAccess: {
      get: false,
      post: false,
      patch: false,
      remove: false
    }
  };

  // DEITY role has full access to everything
  if (role === 'DEITY') {
    relationAccess.methodAccess = {
      get: true,
      post: true,
      patch: true,
      remove: true
    };
    return relationAccess;
  }

  // Check if methodAccess is defined for this relation
  if (relation.methodAccess && Array.isArray(relation.methodAccess)) {
    const roleAccess = relation.methodAccess.find(ma => ma.role === role);

    if (roleAccess) {
      relationAccess.methodAccess = {
        get: !!roleAccess.get,
        post: !!roleAccess.post,
        patch: !!roleAccess.patch,
        remove: !!roleAccess.remove
      };
    }
  }

  return relationAccess;
}

/**
 * Merge two write capability objects using OR semantics
 * @param {object} a - First capability object
 * @param {object} b - Second capability object
 * @returns {object} Merged capabilities with OR semantics for each property
 */
function mergeWriteCapabilities(a, b) {
  return {
    getHint: a.getHint || b.getHint,
    postHint: a.postHint || b.postHint,
    patchHint: a.patchHint || b.patchHint,
    removeHint: a.removeHint || b.removeHint,
    post: a.post || b.post,
    patch: a.patch || b.patch,
    remove: a.remove || b.remove,
    readOnly: a.readOnly || b.readOnly
  };
}

function emptyWriteCapabilities() {
  return {
    getHint: false,
    postHint: false,
    patchHint: false,
    removeHint: false,
    post: false,
    patch: false,
    remove: false,
    readOnly: false
  };
}

module.exports = { getRouteAccess };
